import React, { useEffect, useRef, useState } from "react";
import { Card, CardHeader, CardBody, Col, Container, Row } from "reactstrap";
import * as FlexmonsterReact from "react-flexmonster";
import moment from "moment";
import { reportsApi } from "../../../services/reportsServices";
import Loader from "../../../components/Loader";
import InformationModal from "../../../components/InformationModal";
import { utils } from "../../../utils/utils";
import PeriodTypeSelector from "../../../components/PeriodTypeSelector";

const laborCategoriesAsColumns = (() => {
  const columns = {};
  const report = [];
  utils.LABOR_CATEGORIES.forEach((laborCategory) => {
    const estimatedName = `${laborCategory.code} - ${laborCategory.name} - Est Hours`;
    const actualName = `${laborCategory.code} - ${laborCategory.name} - Actual Hours`;
    columns[estimatedName] = { type: "string" };
    columns[actualName] = { type: "string" };
    report.push({ uniqueName: estimatedName });
    report.push({ uniqueName: actualName });
  });
  return { columns, report };
})();

const columns = {
  "Job Number": {
    type: "string",
  },
  "Ship Complete Date": {
    type: "string",
  },
  "Invoiced Date": {
    type: "string",
  },
  ...laborCategoriesAsColumns.columns,
};

const report = {
  dataSource: {
    data: [columns],
  },
  slice: {
    rows: [
      {
        uniqueName: "Job Number",
      },
      {
        uniqueName: "Ship Complete Date",
      },
      {
        uniqueName: "Invoiced Date",
      },
      ...laborCategoriesAsColumns.report,
    ],
    expands: {
      expandAll: true,
    },
  },
  options: {
    configuratorActive: false,
    grid: {
      type: "flat",
      showTotals: "off",
      showGrandTotals: "off",
      title: "Labor Code Totals",
    },
  },
};

const LaborCodeTotalsReport = () => {
  const pivot = useRef(null);
  const currentYear = moment().year();
  const options = utils.generateYearOptions();
  const [optionSelected, setOptionSelected] = useState({
    label: currentYear,
    value: currentYear,
  });
  const [loading, setLoading] = useState();
  const [informationModal, setInformationModal] = useState({
    isOpen: false,
    title: "",
    body: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        let result = await reportsApi.laborCodeTotals({
          year: optionSelected.value,
        });
        if (pivot) {
          pivot.current.flexmonster.updateData({
            data: [columns, ...result.data],
          });
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
        return setInformationModal({
          isOpen: true,
          title: "Error",
          body: "There was an error with your request.",
        });
      }
    };
    fetchData();
  }, [pivot, optionSelected]);

  return (
    <Container fluid className="d-flex flex-column flex-grow-1">
      <Row className="flex-grow-1">
        <Col className="d-flex flex-column">
          <Card className="flex-grow-1">
            <CardHeader className="d-flex align-items-center justify-content-between">
              <h3 className="mb-0">Labor Code Totals</h3>
              <div className="d-flex align-items-center">
                {loading ? (
                  <div className="min-width-50">
                    <Loader size="sm" className="mr-3" />
                  </div>
                ) : null}
                <div className="">
                  <PeriodTypeSelector
                    options={options}
                    periodType={optionSelected}
                    setPeriodType={setOptionSelected}
                  />
                </div>
              </div>
            </CardHeader>
            <CardBody className="d-flex flex-column flex-grow-1">
              <div className="rounded border height-100">
                <FlexmonsterReact.Pivot
                  key={currentYear}
                  ref={pivot}
                  licenseKey={process.env.REACT_APP_FLEX_M_KEY}
                  toolbar={true}
                  height="100%"
                  report={report}
                  beforetoolbarcreated={(toolbar) =>
                    utils.customizeToolbarReport(
                      toolbar,
                      pivot,
                      "labor_code_totals_report"
                    )
                  }
                />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      {informationModal.isOpen ? (
        <InformationModal
          {...informationModal}
          onClose={() => setInformationModal({ isOpen: false })}
        />
      ) : null}
    </Container>
  );
};

export default LaborCodeTotalsReport;
